import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutationState } from '@tanstack/react-query';

import type { DesiredSettingValue } from '@crossnokaye/typescript-sdk/models/components';

import { getSaveProgressStats } from './save-progress.utils';
import {
  deriveSaveProgressByChangeId,
  type UpdateSettingsMutationEntry,
} from './save-session-progress';
import { CONCURRENT_SAVE_LIMIT, saveSettingsConcurrently } from './save-settings-concurrently';
import type { PreviewSaveStatus } from './setting-preview.types';
import type { SettingChange } from './settings-save.types';

interface SaveCompleteResult {
  completed: string[];
  failed: string[];
  cancelled: string[];
}

const DEFAULT_MUTATION_KEY = ['updateSettings'] as const;

const defaultGetItemId = <TPayload>(item: TPayload): string =>
  (item as DesiredSettingValue).settingId;

const defaultGetVariableItemIds = (vars: unknown): string[] => {
  if (!Array.isArray(vars)) return [];
  return vars.map((item) => (item as DesiredSettingValue).settingId);
};

export interface UseSettingsSaveOptions<TPayload = DesiredSettingValue> {
  changes: SettingChange<TPayload>[];
  updateFn: (items: TPayload[]) => Promise<unknown>;
  onSaveComplete: (result: SaveCompleteResult) => void;
  mutationKey?: readonly string[];
  getItemId?: (item: TPayload) => string;
  getVariableItemIds?: (vars: unknown) => string[];
}

export interface UseSettingsSaveReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  confirmSave: () => Promise<void>;
  cancelPending: () => void;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
  hasPendingSave: boolean;
  saveComplete: boolean;
  progressStats: ReturnType<typeof getSaveProgressStats>;
}

export const useSettingsSave = <TPayload = DesiredSettingValue>({
  changes,
  onSaveComplete,
  updateFn,
  mutationKey = DEFAULT_MUTATION_KEY,
  getItemId = defaultGetItemId,
  getVariableItemIds = defaultGetVariableItemIds,
}: UseSettingsSaveOptions<TPayload>): UseSettingsSaveReturn => {
  const [isOpen, setIsOpen] = useState(false);

  // Save session state
  const [targetSettingIds, setTargetSettingIds] = useState<Set<string>>(new Set());
  const [queuedBySettingId, setQueuedBySettingId] = useState<Set<string>>(new Set());
  const [cancelledBySettingId, setCancelledBySettingId] = useState<Set<string>>(new Set());
  const [saveRunStartedAt, setSaveRunStartedAt] = useState<number | null>(null);

  // Refs for stable references in async flows
  const runIdCounterRef = useRef(0);
  const activeRunIdRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const settingIdToChangeIdsRef = useRef<Map<string, string[]>>(new Map());
  const hadSaveAttemptRef = useRef(false);

  const getItemIdRef = useRef(getItemId);
  getItemIdRef.current = getItemId;

  const mutationKeyFilter = useMemo(() => ({ mutationKey: [...mutationKey] }), [mutationKey]);

  // Progress tracking via TanStack Query mutation state
  const mutationEntries = useMutationState({
    filters: mutationKeyFilter,
    select: (mutation) => ({
      status: mutation.state.status,
      submittedAt: mutation.state.submittedAt,
      variables: mutation.state.variables,
    }),
  }) as UpdateSettingsMutationEntry[];

  const saveProgressByChangeId = useMemo(
    () =>
      deriveSaveProgressByChangeId({
        targetSettingIds,
        queuedBySettingId,
        cancelledBySettingId,
        mutationEntries,
        saveRunStartedAt,
        settingIdToChangeIds: settingIdToChangeIdsRef.current,
        getVariableItemIds,
      }),
    [
      targetSettingIds,
      queuedBySettingId,
      cancelledBySettingId,
      mutationEntries,
      saveRunStartedAt,
      getVariableItemIds,
    ]
  );

  const progressStats = useMemo(
    () => getSaveProgressStats(saveProgressByChangeId),
    [saveProgressByChangeId]
  );

  const hasPendingSave = useMemo(
    () => Object.values(saveProgressByChangeId).some((s) => s === 'pending' || s === 'queued'),
    [saveProgressByChangeId]
  );

  const saveComplete = useMemo(
    () => progressStats.total > 0 && progressStats.isComplete,
    [progressStats]
  );

  // Actions
  const open = useCallback(() => setIsOpen(true), []);

  // Use ref for progress to avoid re-creating close on every progress update
  const saveProgressRef = useRef(saveProgressByChangeId);
  saveProgressRef.current = saveProgressByChangeId;

  const close = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;

    if (hadSaveAttemptRef.current) {
      const completed: string[] = [];
      const failed: string[] = [];
      const cancelled: string[] = [];

      for (const [changeId, status] of Object.entries(saveProgressRef.current)) {
        if (status === 'completed') completed.push(changeId);
        else if (status === 'failed') failed.push(changeId);
        else if (status === 'cancelled') cancelled.push(changeId);
      }

      onSaveComplete({ completed, failed, cancelled });
    }

    setTargetSettingIds(new Set());
    setQueuedBySettingId(new Set());
    setCancelledBySettingId(new Set());
    setSaveRunStartedAt(null);
    activeRunIdRef.current = null;
    settingIdToChangeIdsRef.current = new Map();
    hadSaveAttemptRef.current = false;
    setIsOpen(false);
  }, [onSaveComplete]);

  const confirmSave = useCallback(async () => {
    if (changes.length === 0) return;
    if (activeRunIdRef.current !== null) return;

    hadSaveAttemptRef.current = true;
    const currentRunId = ++runIdCounterRef.current;
    activeRunIdRef.current = currentRunId;

    const settingIdToChangeIds = new Map<string, string[]>();
    for (const change of changes) {
      const existing = settingIdToChangeIds.get(change.settingId) ?? [];
      existing.push(change.changeId);
      settingIdToChangeIds.set(change.settingId, existing);
    }
    settingIdToChangeIdsRef.current = settingIdToChangeIds;

    const allSettingIds = new Set(changes.map((c) => c.settingId));
    setTargetSettingIds(allSettingIds);
    setQueuedBySettingId(new Set(allSettingIds));
    setCancelledBySettingId(new Set());
    setSaveRunStartedAt(Date.now());

    const controller = new AbortController();
    abortRef.current = controller;

    const desiredValues = changes.map((c) => c.desiredValue);

    await saveSettingsConcurrently({
      settings: desiredValues,
      updateFn: (items) => {
        if (activeRunIdRef.current === currentRunId) {
          setQueuedBySettingId((prev) => {
            const next = new Set(prev);
            for (const item of items) {
              next.delete(getItemIdRef.current(item));
            }
            return next;
          });
        }
        return updateFn(items);
      },
      concurrency: CONCURRENT_SAVE_LIMIT,
      signal: controller.signal,
      getItemId: getItemIdRef.current,
    });

    if (activeRunIdRef.current === currentRunId) {
      setQueuedBySettingId(new Set());
    }
  }, [changes, updateFn]);

  const cancelPending = useCallback(() => {
    setQueuedBySettingId((currentQueued) => {
      setCancelledBySettingId((prev) => new Set([...prev, ...currentQueued]));
      abortRef.current?.abort();
      return new Set();
    });
  }, []);

  return {
    isOpen,
    open,
    close,
    confirmSave,
    cancelPending,
    saveProgressByChangeId,
    hasPendingSave,
    saveComplete,
    progressStats,
  };
};
